import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { UIPreviewBannerService } from './ui-preview-banner.service';
import { UIPreviewKey, UIPreviewToggleService } from './ui-preview-toggle.service';
import { MenuState } from '../../constants/menu-state.enum';
import { MenuService } from '../menu.service';
import { AccountsService } from '../accounts.service';

/**
 * Service responsible for managing UI preview functionality within the application.
 * It listens for preview toggle events and handles them by updating the UI to either
 * preview or revert a new UI feature
 * It also determines when the UI preview banner should be displayed based on the
 * current page by listening to navigation events. 
 * 
 * Usefull for determining if the UI preview banner should be displayed for a feature
 * housed in another SPA.
 * If a local feature is added, this service can be used or the component can directly
 * interface with `UIPreviewBannerService` to show the banner.
 * 
 * All handling for turning on or off the preview should be done in this service.
 * 
 * Features that are previewable should be registered in the `UIPreviewKey` enum.
 */
@Injectable({
  providedIn: 'root'
})
export class UIPreviewService {
  get accountId() {
    return this.accountsService.getCurrentAccount().accountId;
  }

  constructor(
    private toggleService: UIPreviewToggleService,
    private router: Router,
    private bannerService: UIPreviewBannerService,
    private menuService: MenuService,
    private accountsService: AccountsService,
  ) {
    this.init();
  }

  private init() {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => this.handleNavigationEnd(event));
    this.toggleService.previewToggled$.subscribe(({ key, enabled }) => this.handlePreviewToggle(key, enabled));
  }

  private handleNavigationEnd(event: NavigationEnd) {
    this.bannerService.hideBanner();
    if (event.url.endsWith('numbers')
      || event.url.endsWith('departments-and-locations')
      || event.url.endsWith('emergency-services')
      || event.url.endsWith('voice-gateway')
      || event.url.endsWith('tools')
    ) {
      this.bannerService.showBanner(UIPreviewKey.SYSTEM_SETTINGS, true);
    } else if (event.url.endsWith('mivb/systemSettings')) { // Only mivb system settings, not osv, os4k, or mivo5000
      this.bannerService.showBanner(UIPreviewKey.SYSTEM_SETTINGS, false);
    }

    // So far only the system settings page has a preview
    // if there are more pages with previews, we can add them here
  }

  private handlePreviewToggle(key: UIPreviewKey, enabled: boolean) {
    return enabled ? this.handleToggleOn(key) : this.handleToggleOff(key);
  }

  // Add more cases here as we add more previewable features
  // They can be handled in any way, doesn't have to be a navigation
  private handleToggleOn(key: UIPreviewKey) {
    switch (key) {
      case UIPreviewKey.SYSTEM_SETTINGS: return this.resetMenuItemsAndNavigate(['accounts', this.accountId, 'mivb', 'numbers']);
      default: return;
    }
  }

  // Add more cases here as we add more previewable features
  // They can be handled in any way, doesn't have to be a navigation
  private handleToggleOff(key: UIPreviewKey) {
    switch (key) {
      case UIPreviewKey.SYSTEM_SETTINGS: return this.resetMenuItemsAndNavigate(['accounts', this.accountId, 'mivb', 'systemSettings']);
      default: return;
    }
  }

  async resetMenuItemsAndNavigate(route: string[]) {
    this.menuService.initialRoute = route;  // updated to avoid some odd navigation behaviour from the AppComponent
                                            // and to ensure the menu is reset when using the company switcher
    return this.menuService.checkForFeatureSpecificMenusForAccount({accountId: this.accountId} as any)
    .then(() => this.menuService.setState(MenuState.AccountAdmin, this.accountId, route))
    // Technically the final navigation is not needed
    // The AppComponent subscribes to menu state changes and will navigate to the initalRoute if it is set
    // However, that behaviour could change so lets guarantee the navigation happens
    // This is a good example of side effects in Angular and why we should be careful with them
    .then(() => this.router.navigate(route))
  }
}
